import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, AlertCircle, Settings, GitBranch, Loader2, RefreshCw } from 'lucide-react';
import { formatDate } from '../utils/timezone';
import { incidentAPI, serviceRequestAPI, changeRequestAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Paginator from '../components/ui/Paginator';

const stClass = {
  new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-purple-100 text-purple-800', resolved: 'bg-emerald-200 text-emerald-900',
  closed: 'bg-slate-200 text-slate-600', cancelled: 'bg-rose-100 text-rose-700',
};
const prClass = { p1: 'bg-rose-100 text-rose-800', p2: 'bg-amber-100 text-amber-800', p3: 'bg-blue-100 text-blue-800', p4: 'bg-slate-200 text-slate-700' };

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | incident | sr | cr

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = { reporter: user.id, ordering: '-created_at', page_size: 100 };
      const [incRes, srRes, crRes] = await Promise.allSettled([
        incidentAPI.list(params),
        serviceRequestAPI.list(params),
        changeRequestAPI.list(params),
      ]);
      const all = [];
      if (incRes.status === 'fulfilled') (incRes.value.data?.results || incRes.value.data || []).forEach(t => all.push({ ...t, _type: 'incident' }));
      if (srRes.status === 'fulfilled') (srRes.value.data?.results || srRes.value.data || []).forEach(t => all.push({ ...t, _type: 'sr' }));
      if (crRes.status === 'fulfilled') (crRes.value.data?.results || crRes.value.data || []).forEach(t => all.push({ ...t, _type: 'cr' }));
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTickets(all);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]); // eslint-disable-line

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t._type === filter);
  const counts = { all: tickets.length, incident: tickets.filter(t => t._type === 'incident').length, sr: tickets.filter(t => t._type === 'sr').length, cr: tickets.filter(t => t._type === 'cr').length };

  // Client-side pagination on filtered results
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const pagedTickets = filtered.slice((page - 1) * pageSize, page * pageSize);

  const typeIcon = { incident: AlertCircle, sr: Settings, cr: GitBranch };
  const typeColor = { incident: 'text-rose-600', sr: 'text-blue-600', cr: 'text-purple-600' };
  const typeTag = { incident: 'bg-rose-100 text-rose-800', sr: 'bg-blue-100 text-blue-800', cr: 'bg-purple-100 text-purple-800' };

  return (
    <div data-testid="my-tickets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Ticket className="w-6 h-6 text-sky-500" /> My Tickets
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Tickets you raised — Incidents, Service Requests, Change Requests</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm bg-white dark:bg-slate-800 border dark:border-slate-700 hover:bg-slate-50 text-slate-600 dark:text-slate-300">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl w-fit">
        {[['all','All'], ['incident','Incidents'], ['sr','Service Req.'], ['cr','Change Req.']].map(([val, label]) => (
          <button key={val} onClick={() => { setFilter(val); setPage(1); }} data-testid={`filter-${val}`}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === val ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
            {label} <span className="ml-1 text-xs opacity-60">({counts[val]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <Ticket className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
          <p className="text-slate-500">You haven't raised any {filter === 'all' ? '' : filter.toUpperCase()} tickets yet.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="my-tickets-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Ticket</th>
                <th className="px-4 py-3 text-left font-semibold">Title</th>
                <th className="px-4 py-3 text-center font-semibold">Type</th>
                <th className="px-4 py-3 text-center font-semibold">Priority</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Application</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {pagedTickets.map(t => {
                const Icon = typeIcon[t._type] || AlertCircle;
                return (
                  <tr key={`${t._type}-${t.id}`} onClick={() => navigate(`/ticket/${t._type}/${t.id}`)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer" data-testid={`row-${t.ticket_number}`}>
                    <td className="px-4 py-3 font-mono font-bold text-sky-700 dark:text-sky-400">{t.ticket_number}</td>
                    <td className="px-4 py-3 dark:text-white max-w-xs truncate">{t.title}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${typeTag[t._type]}`}>
                        <Icon className={`w-3 h-3 ${typeColor[t._type]}`} />{t._type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t._type === 'incident' && t.priority && <span className={`${prClass[t.priority] || 'bg-slate-100'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{t.priority.toUpperCase()}</span>}
                      {t._type === 'sr' && t.urgency_level && <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">{t.urgency_level.replace(/_/g, ' ')}</span>}
                      {t._type === 'cr' && (t.cr_priority || t.risk_level) && <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs font-semibold">{(t.cr_priority || t.risk_level)}</span>}
                      {!t.priority && !t.urgency_level && !t.cr_priority && <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`${stClass[t.status] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs`}>{t.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{t.application_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{t.created_at ? formatDate(t.created_at) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Paginator total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
        </div>
      )}
    </div>
  );
}
